import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'

const progressShrink = keyframes`
  from {
    width: 100%;
  }

  to {
    width: 0%;
  }
`

const psToastTypePalette = {
  info: {
    accent: '#1a56db',
    iconBackground: '#dbeafe',
    iconColor: '#1d4ed8',
    primaryBorder: '#bfdbfe',
    primaryBackground: '#eff6ff',
  },
  warning: {
    accent: '#f59e0b',
    iconBackground: '#fef3c7',
    iconColor: '#b45309',
    primaryBorder: '#fcd34d',
    primaryBackground: '#fffbeb',
  },
  error: {
    accent: '#ef4444',
    iconBackground: '#fee2e2',
    iconColor: '#dc2626',
    primaryBorder: '#fecaca',
    primaryBackground: '#fef2f2',
  },
}

const getPalette = (type) => psToastTypePalette[type] || psToastTypePalette.info

const getIcon = (type) => {
  switch (type) {
    case 'warning':
      return '!'
    case 'error':
      return 'x'
    default:
      return 'i'
  }
}

const formatToastTime = () =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date())

const ToastFrame = styled.div`
  width: 340px;
  display: flex;
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
`

const ToastAccent = styled.div`
  width: 4px;
  flex-shrink: 0;
  background: ${({ notificationType }) => getPalette(notificationType).accent};
`

const ToastIconWrap = styled.div`
  width: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 14px;
`

const ToastIconInner = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ notificationType }) => getPalette(notificationType).iconBackground};
  color: ${({ notificationType }) => getPalette(notificationType).iconColor};
`

const ToastBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 12px 16px 12px 8px;
`

const ToastTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3px;
`

const ToastTitle = styled.span`
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
`

const ToastTime = styled.span`
  margin-top: 1px;
  color: #94a3b8;
  font-size: 10px;
  white-space: nowrap;
  flex-shrink: 0;
`

const ToastDescription = styled.div`
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: ${({ hasActions }) => (hasActions ? '8px' : '0')};
  white-space: pre-wrap;
`

const ToastActions = styled.div`
  display: flex;
  gap: 4px;
`

const ToastButton = styled.button`
  background: ${({ primary, notificationType }) =>
    primary ? getPalette(notificationType).primaryBackground : 'transparent'};
  border: 0.5px solid
    ${({ primary, notificationType }) =>
      primary ? getPalette(notificationType).primaryBorder : '#e2e8f0'};
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
  color: ${({ primary, notificationType }) =>
    primary ? getPalette(notificationType).accent : '#0f172a'};

  &:hover {
    background: ${({ primary, notificationType }) =>
      primary ? getPalette(notificationType).primaryBackground : '#f1f5f9'};
  }
`

const ToastClose = styled.button`
  border: none;
  background: transparent;
  align-self: flex-start;
  padding: 10px 12px 0 0;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.12s ease;

  &:hover {
    color: #475569;
  }
`

const ToastProgress = styled.div`
  position: absolute;
  left: 4px;
  right: 0;
  bottom: 0;
  height: 2px;
  background: #f1f5f9;
`

const ToastProgressBar = styled.div`
  height: 100%;
  background: ${({ notificationType }) => getPalette(notificationType).accent};
  animation: ${progressShrink} ${({ durationMs }) => `${durationMs}ms`} linear forwards;
`

const NotificationToast = ({
  type = 'info',
  title,
  description,
  actionLabel,
  onAction,
  onDismiss,
  durationMs = 4000,
}) => {
  const hasPrimaryAction = typeof onAction === 'function' && !!actionLabel

  useEffect(() => {
    if (!onDismiss || !durationMs) return undefined

    const timeoutId = window.setTimeout(() => {
      onDismiss()
    }, durationMs)

    return () => window.clearTimeout(timeoutId)
  }, [durationMs, onDismiss])

  const handleAction = () => {
    if (hasPrimaryAction) onAction()
    if (onDismiss) onDismiss()
  }

  return (
    <ToastFrame>
      <ToastAccent notificationType={type} />
      <ToastIconWrap>
        <ToastIconInner notificationType={type}>{getIcon(type)}</ToastIconInner>
      </ToastIconWrap>
      <ToastBody>
        <ToastTop>
          <ToastTitle>{title}</ToastTitle>
          <ToastTime>{formatToastTime()}</ToastTime>
        </ToastTop>
        {description ? <ToastDescription hasActions={hasPrimaryAction}>{description}</ToastDescription> : null}
        <ToastActions>
          {hasPrimaryAction ? (
            <ToastButton type="button" primary notificationType={type} onClick={handleAction}>
              {actionLabel}
            </ToastButton>
          ) : null}
          <ToastButton type="button" notificationType={type} onClick={onDismiss}>
            Dispensar
          </ToastButton>
        </ToastActions>
      </ToastBody>
      <ToastClose type="button" onClick={onDismiss}>
        x
      </ToastClose>
      <ToastProgress>
        <ToastProgressBar notificationType={type} durationMs={durationMs} />
      </ToastProgress>
    </ToastFrame>
  )
}

export default NotificationToast
